import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import {
    CustomInput,
    CustomSelect,
    CustomToggle,
    CustomTextarea
  } from '../Components/CustomFormElements';
import Button from '../Components/Buttons';
import { AlertProvider, useAlert } from '../Components/AlertContext';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import PageLoader from '../Components/PageLoader';
import { isEmpty } from '../utils/validationUtil';
import {ConnectionApiProvider, useConnectionApi} from "../Services/ConnectionApiProvider";
import ConnectionHeadersAttributes from "../Components/ConnectionAttributes";

function ConnectionDetail({canEdit}) {
    const navigate = useNavigate();
    const location = useLocation();
    const { organizationId, connectionId } = useParams();
    const { showAlert } = useAlert();
    const { showConfirmation } = useConfirmation();

    const {
        createConnection,
        updateConnectionById,
        getConnectionById,
        getConnectionSecretsById,
        getGoogleAccountConstants,
        disconnectGoogleAccount,
    } = useConnectionApi();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isNew, setIsNew] = useState(true);
    const [isDisabled, setIsDisabled] = useState(true);

    const [connectionName, setConnectionName] = useState();
    const [isActive, setIsActive] = useState(true);
    const [connectionType, setConnectionType] = useState();
    const [connectionSecrets, setConnectionSecrets] = useState({});
    const [zendeskDomain, setZendeskDomain] = useState();
    const [cardUrlA2A, setCardUrlA2A] = useState();
    const [creatioBaseUrl, setCreatioBaseUrl] = useState();
    const [creatioTokenUrl, setCreatioTokenUrl] = useState();
    const [creatioScope, setCreatioScope] = useState();
    const [keyCrmBaseUrl, setKeyCrmBaseUrl] = useState();
    const [mcpBaseUrl, setMcpBaseUrl] = useState();
    const [notionWorkspaceId, setNotionWorkspaceId] = useState();
    const [headerAttributes, setHeaderAttributes] = useState([]);

    const [googleAuthUrl, setGoogleAuthUrl] = useState();
    const [isGoogleConnected, setIsGoogleConnected] = useState(false);

    const updateSecrets = (newSecrets) => {
        setConnectionSecrets((prevSecrets) => ({
            ...prevSecrets,
            ...newSecrets,
        }));
    };

    useEffect(() => {
        const func = async () => {
            setLoading(true);
            try {
                if (connectionId !== 'new-connection') {
                    const response = await getConnectionById(connectionId);
                    const secrets = await getConnectionSecretsById(connectionId);
                    setConnectionSecrets(secrets);
                    setConnectionName(response.name);
                    setZendeskDomain(response.zendesk_domain);
                    setCardUrlA2A(response.a2a_card_url);
                    setCreatioTokenUrl(response.creatio_token_url);
                    setCreatioBaseUrl(response.creatio_base_url);
                    setKeyCrmBaseUrl(response.key_crm_base_url);
                    setMcpBaseUrl(response.mcp_base_url);
                    setCreatioScope(response.creatio_scope);
                    setIsActive(response.is_active);
                    setConnectionType(response.type);
                    setNotionWorkspaceId(response.notion_workspace_id);
                    setIsGoogleConnected(response.is_google_connected)
                    setHeaderAttributes(response.header_attributes || []);

                    if (["GOOGLE_DRIVE", "GOOGLE_CALENDAR"].includes(response.type)) {
                        try {
                            const constants = await getGoogleAccountConstants(connectionId, response.type);
                            setGoogleAuthUrl(constants.google_auth_url);
                        } catch (err) {
                            console.error('Error fetching Google account constants:', err);
                        }
                    }
                }
            } catch (error) {
                setError('Error fetching connection data');
                console.error('Error fetching connection data:', error);
            }
            finally {
                setLoading(false);
            }
        }
        func();
    }, [connectionId]);

    useEffect(() => {
        setIsNew(connectionId === 'new-connection');
        setIsDisabled(!canEdit);
    }, [connectionId])


    const onSave = async () => {
        if (isEmpty(connectionName)) {
            showAlert('fail', 'Connection name is required');
            return;
        }
        if (isEmpty(connectionType)) {
            showAlert('fail', 'Connection type is required');
            return;
        }

        if (connectionType === 'ZENDESK') {
            if (isEmpty(zendeskDomain)) {
                showAlert("fail", "Zendesk domain is required");
                return;
            }
        }

        if (connectionType === 'A2A') {
            if (isEmpty(cardUrlA2A)) {
                showAlert("fail", "A2A Card URL is required");
                return;
            }
        }

        if (connectionType === 'CREATIO') {
            if (isEmpty(creatioBaseUrl)) {
                showAlert("fail", "Creatio base url is required");
                return;
            }
            if (isEmpty(creatioTokenUrl)) {
                showAlert("fail", "Creatio token url is required");
                return;
            }
        }

        if (connectionType === 'NOTION') {
            if (isEmpty(notionWorkspaceId)) {
                showAlert("fail", "Notion Workspace Id is required");
                return;
            }

            if (isEmpty(connectionSecrets?.notion_access_token)) {
                showAlert("fail", "Notion Access Token is required");
                return;
            }
        }

        let connection = {
            name: connectionName,
            type: connectionType,
            is_active: isActive,
            zendesk_domain: zendeskDomain,
            a2a_card_url: cardUrlA2A,
            creatio_base_url: creatioBaseUrl,
            creatio_token_url: creatioTokenUrl,
            creatio_scope: creatioScope,
            key_crm_base_url: keyCrmBaseUrl,
            notion_workspace_id: notionWorkspaceId,
            mcp_base_url: mcpBaseUrl,
        }

        connection = {
            ...connection,
            ...connectionSecrets,
        };

        if (connectionType === 'MCP') {
            connection.header_attributes = headerAttributes;
        }

        setLoading(true);

        try {
            if (isNew) {
                await createConnection(connection);
                showAlert('success', 'Connection created successfully');
            } else {
                await updateConnectionById(connectionId, connection);
                showAlert('success', 'Connection updated successfully');
            }
            navigate(`/organizations/${organizationId}/connections`);
        } catch (error) {
            console.error('Error saving connection:', error);
            showAlert('fail', 'Failed to save connection');
        }
        finally {
            setLoading(false);
        }
    }

    const onCancel = () => {
        navigate(`/organizations/${organizationId}/connections`);
    };

    if (loading) {
        return <PageLoader />;
    }

    return (
        <div className="container">
            <Breadcrumbs />
            <div className="row d-flex gap">
                <div className="radius-yes p-2 bg-yes col-6">
                    <h2 className='h2-title'>{connectionName}</h2>
                    <div className='gap d-flex flex-row-to-col flex-column'>
                        <div className='int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr'>
                            <h4 className='h4-title'>Main Settings</h4>

                            <div className='input-block d-flex flex-row justify-between'>
                                <div className='input-description'>
                                    <div className='input-title'>Connection name</div>
                                    <div className='small-font'>Enter connection name</div>
                                </div>
                                <div className='align-wrapper d-flex align-center'>
                                    <CustomInput
                                        id="chat-name"
                                        label="Connection Name"
                                        placeholder="Enter connection name"
                                        type="text"
                                        icon={<i className="fa-solid fa-comments"></i>}
                                        value={connectionName}
                                        onChange={(id, value) => setConnectionName(value)}
                                        disabled={isDisabled}
                                    />
                                </div>
                            </div>

                            <div className='input-row input-block d-flex flex-row justify-between'>
                                <div className='input-description'>
                                    <div className='input-title'>Connection Type</div>
                                    <div className='small-font'>Select connection type</div>
                                </div>
                                <div className='align-wrapper d-flex align-center'>
                                    <CustomSelect
                                        id='connection-type'
                                        label="Connection Type"
                                        options={[
                                            { value: 'NOTION', label: 'Notion' },
                                            { value: 'KEYCRM', label: 'CRM - KeyCRM' },
                                            { value: 'CREATIO', label: 'CRM - Creatio' },
                                            { value: 'JIRA', label: 'Jira' },
                                            { value: 'TRELLO', label: 'Trello' },
                                            { value: 'ZENDESK', label: 'Zendesk' },
                                            {value: 'A2A', label: 'A2A'},
                                            {value: 'GOOGLE_DRIVE', label: 'Google Drive'},
                                            {value: 'GOOGLE_CALENDAR', label: 'Google Calendar'},
                                            {value: 'MCP', label: 'MCP'},
                                        ]}
                                        value={connectionType}
                                        onChange={(value) => {
                                            setConnectionType(value)
                                        }}
                                        multiSelect={false}
                                        disabled={isDisabled || !isNew}
                                    />
                                </div>
                            </div>
                            {connectionType === "NOTION" && (
                                <>
                                    <div className="input-block d-flex flex-row justify-between">
                                      <div className="input-description">
                                          <div className="input-title">Notion Workspace Id</div>
                                          <div className="small-font">Notion Workspace Id</div>
                                      </div>
                                      <div className="align-wrapper d-flex align-center">
                                          <CustomInput
                                            id="notion-workspace-id"
                                            label="Notion Workspace Id"
                                            placeholder="Enter workspace id"
                                            icon={<i className="fa-brands fa-telegram"></i>}
                                            value={notionWorkspaceId}
                                            onChange={(id, value) => setNotionWorkspaceId(value)}
                                            disabled={isDisabled}
                                            type="text"
                                          />
                                      </div>
                                    </div>
                                    <div className="input-block d-flex flex-row justify-between">
                                      <div className="input-description">
                                          <div className="input-title">Notion Access Token</div>
                                          <div className="small-font">Notion Access Token</div>
                                      </div>
                                      <div className="align-wrapper d-flex align-center">
                                          <CustomInput
                                            id="notion-access-token"
                                            label="Notion Access Token"
                                            placeholder="Enter token"
                                            icon={<i className="fa-brands fa-telegram"></i>}
                                            value={connectionSecrets?.notion_access_token}
                                            onChange={(id, value) => updateSecrets({notion_access_token: value})}
                                            disabled={isDisabled}
                                            type="text"
                                          />
                                      </div>
                                    </div>
                                </>
                            )}
                            {connectionType === "A2A" && (
                                <>
                                    <div className="input-block d-flex flex-row justify-between">
                                        <div className="input-description">
                                            <div className="input-title">Card URL</div>
                                            <div className="small-font">Enter Card URL</div>
                                        </div>
                                        <div className="align-wrapper d-flex align-center">
                                            <CustomInput
                                                id="a2a-url"
                                                label="Card URL"
                                                placeholder="Enter Card URL"
                                                value={cardUrlA2A}
                                                onChange={(id, value) => setCardUrlA2A(value)}
                                                disabled={isDisabled}
                                                type="text"
                                            />
                                        </div>
                                    </div>

                                </>

                            )}

                            {connectionType === "ZENDESK" && (
                                <>
                                    <div className="input-block d-flex flex-row justify-between">
                                        <div className="input-description">
                                            <div className="input-title">Zendesk Domain</div>
                                            <div className="small-font">Enter Zendesk Domain</div>
                                        </div>
                                        <div className="align-wrapper d-flex align-center">
                                            <CustomInput
                                                id="zendesk-domain"
                                                label="Zendesk Domain"
                                                placeholder="Enter domain"
                                                icon={<i className="fa-brands fa-telegram"></i>}
                                                value={zendeskDomain}
                                                onChange={(id, value) => setZendeskDomain(value)}
                                                disabled={isDisabled}
                                                type="text"
                                            />
                                      </div>
                                  </div>
                                  <div className="input-block d-flex flex-row justify-between">
                                      <div className="input-description">
                                          <div className="input-title">Zendesk Ticket Email Key</div>
                                          <div className="small-font">Enter Zendesk Ticket Email Key</div>
                                      </div>
                                      <div className="align-wrapper d-flex align-center">
                                          <CustomInput
                                            id="zendesk-ticket-email-key"
                                            label="Zendesk Ticket Email Key"
                                            placeholder="Enter token"
                                            icon={<i className="fa-brands fa-telegram"></i>}
                                            value={connectionSecrets?.zendesk_ticket_key_email}
                                            onChange={(id, value) => updateSecrets({zendesk_ticket_key_email: value})}
                                            disabled={isDisabled}
                                            type="text"
                                          />
                                      </div>
                                  </div>
                                  <div className="input-block d-flex flex-row justify-between">
                                      <div className="input-description">
                                          <div className="input-title">Zendesk Ticket Key</div>
                                          <div className="small-font">Enter Zendesk Ticket Key</div>
                                      </div>
                                      <div className="align-wrapper d-flex align-center">
                                          <CustomInput
                                            id="zendesk-ticket-key"
                                            label="Zendesk Ticket Key"
                                            placeholder="Enter token"
                                            icon={<i className="fa-brands fa-telegram"></i>}
                                            value={connectionSecrets?.zendesk_ticket_key}
                                            onChange={(id, value) => updateSecrets({zendesk_ticket_key: value})}
                                            disabled={isDisabled}
                                            type="text"
                                          />
                                      </div>
                                  </div>
                              </>
                            )}

                            {connectionType === "CREATIO" && (
                              <>
                                  <div className="input-block d-flex flex-row justify-between">
                                      <div className="input-description">
                                          <div className="input-title">Creatio base url</div>
                                          <div className="small-font">Enter creatio base url</div>
                                      </div>
                                      <div className="align-wrapper d-flex align-center">
                                          <CustomInput
                                            id="creatio-base-url"
                                            label="Creatio base url"
                                            placeholder="Enter creatio base url"
                                            icon={<i className="fa-brands fa-telegram"></i>}
                                            value={creatioBaseUrl}
                                            onChange={(id, value) => setCreatioBaseUrl(value)}
                                            disabled={isDisabled}
                                            type="text"
                                          />
                                      </div>
                                  </div>
                                  <div className="input-block d-flex flex-row justify-between">
                                      <div className="input-description">
                                          <div className="input-title">Creatio token url</div>
                                          <div className="small-font">Enter creatio token url</div>
                                      </div>
                                      <div className="align-wrapper d-flex align-center">
                                          <CustomInput
                                            id="creatio-token-url"
                                            label="Creatio token url"
                                            placeholder="Enter creatio token url"
                                            icon={<i className="fa-brands fa-telegram"></i>}
                                            value={creatioTokenUrl}
                                            onChange={(id, value) => setCreatioTokenUrl(value)}
                                            disabled={isDisabled}
                                            type="text"
                                          />
                                      </div>
                                  </div>
                                  <div className="input-block d-flex flex-row justify-between">
                                      <div className="input-description">
                                          <div className="input-title">Creatio client id</div>
                                          <div className="small-font">Enter Creatio client id</div>
                                      </div>
                                      <div className="align-wrapper d-flex align-center">
                                          <CustomInput
                                            id="creatio-client-id"
                                            label="Creatio Client Id"
                                            placeholder="Enter creatio client id"
                                            icon={<i className="fa-brands fa-telegram"></i>}
                                            value={connectionSecrets?.creatio_client_id}
                                            onChange={(id, value) => updateSecrets({creatio_client_id: value})}
                                            disabled={isDisabled}
                                            type="text"
                                          />
                                      </div>
                                  </div>

                                  <div className="input-block d-flex flex-row justify-between">
                                      <div className="input-description">
                                          <div className="input-title">Creatio client secret</div>
                                          <div className="small-font">Enter Creatio client secret</div>
                                      </div>
                                      <div className="align-wrapper d-flex align-center">
                                          <CustomInput
                                            id="creatio-client-secret"
                                            label="Creatio Client secret"
                                            placeholder="Enter creatio client secret"
                                            icon={<i className="fa-brands fa-telegram"></i>}
                                            value={connectionSecrets?.creatio_client_secret}
                                            onChange={(id, value) => updateSecrets({creatio_client_secret: value})}
                                            disabled={isDisabled}
                                            type="text"
                                          />
                                      </div>
                                  </div>
                                  <div className="input-block d-flex flex-row justify-between">
                                      <div className="input-description">
                                          <div className="input-title">Creatio scope</div>
                                          <div className="small-font">Enter Creatio scope</div>
                                      </div>
                                      <div className="align-wrapper d-flex align-center">
                                          <CustomInput
                                            id="creatio-scope"
                                            label="Creatio scope"
                                            placeholder="Enter creatio scope"
                                            icon={<i className="fa-brands fa-telegram"></i>}
                                            value={creatioScope}
                                             onChange={(id, value) => setCreatioScope(value)}
                                            disabled={isDisabled}
                                            type="text"
                                          />
                                      </div>
                                  </div>
                              </>
                            )}

                            {connectionType === "KEYCRM" && (
                              <>
                                  <div className="input-block d-flex flex-row justify-between">
                                      <div className="input-description">
                                          <div className="input-title">KeyCRM base url</div>
                                          <div className="small-font">Enter KeyCRM base url</div>
                                      </div>
                                      <div className="align-wrapper d-flex align-center">
                                          <CustomInput
                                            id="keycrm-base-url"
                                            label="KeyCRM base url"
                                            placeholder="Enter KeyCRM base url"
                                            icon={<i className="fa-brands fa-telegram"></i>}
                                            value={keyCrmBaseUrl}
                                            onChange={(id, value) => setKeyCrmBaseUrl(value)}
                                            disabled={isDisabled}
                                            type="text"
                                          />
                                      </div>
                                  </div>

                                  <div className="input-block d-flex flex-row justify-between">
                                      <div className="input-description">
                                          <div className="input-title">KeyCRM api key</div>
                                          <div className="small-font">Enter KeyCRM api key</div>
                                      </div>
                                      <div className="align-wrapper d-flex align-center">
                                          <CustomInput
                                            id="keycrm-api-key"
                                            label="KeyCRM Api key"
                                            placeholder="Enter KeyCRM api key"
                                            icon={<i className="fa-brands fa-telegram"></i>}
                                            value={connectionSecrets?.key_crm_api_key}
                                            onChange={(id, value) => updateSecrets({key_crm_api_key: value})}
                                            disabled={isDisabled}
                                            type="text"
                                          />
                                      </div>
                                  </div>

                              </>
                            )}

                            {connectionType === "MCP" && (
                              <div className="input-block d-flex flex-row justify-between">
                                  <div className="input-description">
                                      <div className="input-title">MCP base url</div>
                                      <div className="small-font">Enter MCP base url</div>
                                  </div>
                                  <div className="align-wrapper d-flex align-center">
                                      <CustomInput
                                        id="mcp-base-url"
                                        label="MCP base url"
                                        placeholder="Enter Mcp base url"
                                        icon={<i className="fa-brands fa-telegram"></i>}
                                        value={mcpBaseUrl}
                                        onChange={(id, value) => setMcpBaseUrl(value)}
                                        disabled={isDisabled}
                                        type="text"
                                      />
                                  </div>
                              </div>
                            )}

                            {["GOOGLE_DRIVE", "GOOGLE_CALENDAR"].includes(connectionType) && !isNew && (
                              !isGoogleConnected ? (
                                <div className="input-block d-flex flex-row justify-between">
                                    <div className="input-description">
                                        <div className="input-title">Authorization window</div>
                                        <div className="small-font">Log in with Google to connect your {connectionType}</div>
                                    </div>
                                    <div className="button-container d-flex justify-end">
                                        <Button
                                          variant="primary"
                                          size="large"
                                          onClick={() => {
                                              localStorage.setItem("connectionId", connectionId);
                                              console.log(googleAuthUrl);
                                              window.location.href = googleAuthUrl;
                                          }}
                                        >
                                            <i className="fa-brands fa-google" style={{marginRight: "8px"}}></i>
                                            Connect Google account
                                        </Button>
                                    </div>
                                </div>
                              ) : (
                                <div className="input-block d-flex flex-row justify-between">
                                    <div className="input-description">
                                        <div className="input-title">Google account</div>
                                        <div className="small-font">
                                            Connected
                                        </div>
                                    </div>
                                    <div className="button-container d-flex justify-end">
                                        <Button
                                          variant="danger"
                                          size="large"
                                          onClick={async () => {
                                              try {
                                                  setLoading(true);
                                                  await disconnectGoogleAccount(connectionId);
                                                  setIsGoogleConnected(false);
                                                  showAlert('success', 'Google account disconnected successfully');
                                              } catch (err) {
                                                  console.error('Failed to disconnect Google account:', err);
                                                  showAlert('fail', 'Failed to disconnect Google account');
                                              } finally {
                                                  setLoading(false);
                                              }
                                          }}
                                        >
                                            <i className="fa-brands fa-google" style={{marginRight: "8px"}}></i>
                                            Disconnect Google account
                                        </Button>
                                    </div>
                                </div>
                              )
                            )}

                            <div className='input-row input-block d-flex flex-row justify-between'>
                                <div className='input-description'>
                                    <div className='input-title'>Activate connection</div>
                                    <div className='small-font'>Set connection to be active or inactive to enable or disable it</div>
                                </div>
                                <div className='align-wrapper d-flex align-center'>
                                    <CustomToggle
                                        id="toggle-status"
                                        label={isActive ? 'Active' : 'Inactive'}
                                        checked={isActive}
                                        onChange={(id, value) => setIsActive(value)}
                                        disabled={false}
                                    />
                                </div>
                            </div>
                        </div>

                        {connectionType === "MCP" && (
                          <ConnectionHeadersAttributes
                            headerAttributes={headerAttributes}
                            setHeaderAttributes={setHeaderAttributes}
                            canEdit={!isDisabled}
                          />
                        )}

                        <div className='buttons-container d-flex justify-end'>
                            <Button variant='primary' size='medium' disabled={isDisabled} onClick={onSave}>
                                Save
                            </Button>
                            <Button variant='danger' size='medium' onClick={onCancel}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

function Connection({canEdit}) {
    return (
        <AlertProvider>
            <ConfirmationProvider>
                <ConnectionApiProvider>
                    <ConnectionDetail canEdit={canEdit}/>
                </ConnectionApiProvider>
            </ConfirmationProvider>
        </AlertProvider>
    );
}

export default Connection;
